import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import { useGetShippingsQuery } from '../services/shippings.service';
import { Option } from '@/components/inputs/AutocompleteInput';

interface UseRecipientsAutocompleteProps {
  search?: string;
  minLength?: number;
}

const useRecipientsAutocomplete = ({
  search,
  minLength = 3,
}: UseRecipientsAutocompleteProps = {}) => {
  const {
    debouncedSearch: recipientSearchTextTrimmed,
    shouldSearch: debouncedShouldFetchRecipientSuggestions,
    isDebouncePending,
  } = useAutocompleteSearch(search, minLength);
  const shouldFetchRecipientSuggestions =
    debouncedShouldFetchRecipientSuggestions;

  const {
    data: shippingsResponse,
    isFetching: isRecipientsFetching,
    error: recipientsError,
  } = useGetShippingsQuery(
    {
      paginated: { page: 1, pageSize: 10 },
      filters: {
        recipient: shouldFetchRecipientSuggestions
          ? recipientSearchTextTrimmed
          : '',
      },
    },
    { skip: !shouldFetchRecipientSuggestions }
  );

  const resolvedRecipientOptions: Option[] = useMemo(() => {
    if (!shippingsResponse?.items) return [];

    const recipientDisplayNameByCaseInsensitiveKey = new Map<string, string>();

    for (const shipping of shippingsResponse.items) {
      const recipientDisplayName = shipping.recipient?.trim();
      if (!recipientDisplayName) continue;

      const caseInsensitiveRecipientKey =
        recipientDisplayName.toLocaleLowerCase('es');

      if (
        !recipientDisplayNameByCaseInsensitiveKey.has(
          caseInsensitiveRecipientKey
        )
      ) {
        recipientDisplayNameByCaseInsensitiveKey.set(
          caseInsensitiveRecipientKey,
          recipientDisplayName
        );
      }
    }

    const uniqueRecipientDisplayNames = [
      ...recipientDisplayNameByCaseInsensitiveKey.values(),
    ];

    uniqueRecipientDisplayNames.sort(
      (firstComparedRecipientName, secondComparedRecipientName) =>
        firstComparedRecipientName.localeCompare(
          secondComparedRecipientName,
          'es',
          { sensitivity: 'base' }
        )
    );

    return uniqueRecipientDisplayNames.map((recipientDisplayName) => ({
      label: recipientDisplayName,
      data: recipientDisplayName,
    }));
  }, [shippingsResponse?.items]);

  const recipientOptions = useStableOptions(resolvedRecipientOptions, {
    isPending: isRecipientsFetching || isDebouncePending,
    enabled: shouldFetchRecipientSuggestions || isDebouncePending,
  });

  const isRecipientsLoading =
    shouldFetchRecipientSuggestions && isRecipientsFetching;

  return {
    recipientOptions,
    isRecipientsLoading,
    recipientsError,
  };
};

export default useRecipientsAutocomplete;
